import { useState, useEffect, useMemo, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import moment from "moment";
import { Button } from "Components/Common/Button/Button";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { GetStates } from "Services/API/GetStates.service";
import { ProviderMedLic } from "Services/API/CreateProvider.service";
import { UpdateStateMedLicense } from "Services/API/ProviderEditView.service";
import { UploadFiles } from "Services/API/UploadFile.service";

const ACCEPTED_DATE_FORMATS = ["MM/DD/YYYY", "M/D/YYYY", "MM-DD-YYYY", "M-D-YYYY"];
const DEFAULT_DOC_STORAGE_KEY = "bulkLicenseDefaultDocUrl";

const parsePaste = (raw, states, existingLicenses) => {
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const toAdd = [];
  const toUpdate = [];
  const errors = [];

  lines.forEach((line, idx) => {
    const rowNum = idx + 1;
    const cols = line.split("\t").map((c) => c.trim());

    if (cols.length < 4) {
      errors.push(
        `Row ${rowNum}: expected 4 tab-separated columns (state, license #, issue date, expiry date), got ${cols.length}`
      );
      return;
    }

    const [stateName, licenseNumber, licensedDateStr, expirationStr] = cols;

    const stateMatch = states.find(
      (s) => s.name && s.name.toLowerCase() === stateName.toLowerCase()
    );
    if (!stateMatch) {
      errors.push(`Row ${rowNum}: unknown state "${stateName}"`);
      return;
    }

    if (!licenseNumber) {
      errors.push(`Row ${rowNum}: license number is empty`);
      return;
    }

    const licensedDate = moment(licensedDateStr, ACCEPTED_DATE_FORMATS, true);
    if (!licensedDate.isValid()) {
      errors.push(
        `Row ${rowNum}: invalid issue date "${licensedDateStr}" (use MM/DD/YYYY)`
      );
      return;
    }

    const expirationDate = moment(expirationStr, ACCEPTED_DATE_FORMATS, true);
    if (!expirationDate.isValid()) {
      errors.push(
        `Row ${rowNum}: invalid expiry date "${expirationStr}" (use MM/DD/YYYY)`
      );
      return;
    }

    const existing = existingLicenses.find(
      (l) => l.licenseState === stateMatch.id
    );

    const base = {
      licenseState: stateMatch.id,
      licenseNumber,
      licensedDate: licensedDate.format("MM/DD/YYYY"),
      licenseExpiration: expirationDate.format("MM/DD/YYYY"),
    };

    if (existing && existing.id && isNaN(Number(existing.id))) {
      const preservedDoc =
        existing.licenseDocument && existing.licenseDocument !== "-"
          ? existing.licenseDocument
          : "";
      toUpdate.push({
        ...base,
        id: existing.id,
        licenseDocument: preservedDoc,
        _stateName: stateMatch.name,
      });
    } else {
      toAdd.push({ ...base, _stateName: stateMatch.name });
    }
  });

  return { toAdd, toUpdate, errors };
};

const stripPreviewFields = (rows) =>
  rows.map(({ _stateName, ...rest }) => rest);

export const BulkAddUpdateLicenses = ({
  isOpen,
  onClose,
  providerId,
  existingLicenses,
  onSuccess,
}) => {
  const [pastedText, setPastedText] = useState("");
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [defaultDocUrl, setDefaultDocUrl] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setPastedText("");
    setSubmitError("");
    setUploadError("");
    try {
      setDefaultDocUrl(localStorage.getItem(DEFAULT_DOC_STORAGE_KEY) || "");
    } catch (e) {
      setDefaultDocUrl("");
    }
    setStatesLoading(true);
    GetStates(1, 100)
      .then((res) => {
        setStates(res?.data || []);
      })
      .catch((err) => {
        console.error("[BulkAddUpdateLicenses][GetStates][API_ERROR] - ", err);
        setSubmitError("Failed to load states. Please close and try again.");
      })
      .finally(() => setStatesLoading(false));
  }, [isOpen]);

  const handleDefaultDocSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadError("");
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await UploadFiles(formData);
      const url = response?.data?.result;
      if (!url) throw new Error("Upload returned no URL");
      setDefaultDocUrl(url);
      try {
        localStorage.setItem(DEFAULT_DOC_STORAGE_KEY, url);
      } catch (e) {
        // localStorage unavailable; URL still works for this session
      }
    } catch (err) {
      console.error("[BulkAddUpdateLicenses][UploadDefault][API_ERROR] - ", err);
      setUploadError(
        err?.response?.data?.errors || err?.message || "Upload failed"
      );
    } finally {
      setUploadingDoc(false);
    }
  };

  const clearDefaultDoc = () => {
    setDefaultDocUrl("");
    try {
      localStorage.removeItem(DEFAULT_DOC_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  };

  const parsed = useMemo(() => {
    if (!pastedText.trim() || states.length === 0) {
      return { toAdd: [], toUpdate: [], errors: [] };
    }
    return parsePaste(pastedText, states, existingLicenses);
  }, [pastedText, states, existingLicenses]);

  const hasContent = parsed.toAdd.length + parsed.toUpdate.length > 0;
  const needsDefaultDoc = parsed.toAdd.length > 0 && !defaultDocUrl;
  const canApply =
    hasContent &&
    parsed.errors.length === 0 &&
    !needsDefaultDoc &&
    !submitting &&
    !statesLoading &&
    !uploadingDoc;

  const handleApply = async () => {
    setSubmitError("");
    setSubmitting(true);
    try {
      if (parsed.toAdd.length > 0) {
        const addPayload = stripPreviewFields(parsed.toAdd).map((row) => ({
          ...row,
          licenseDocument: defaultDocUrl,
        }));
        await ProviderMedLic(providerId, addPayload);
      }
      if (parsed.toUpdate.length > 0) {
        await UpdateStateMedLicense(stripPreviewFields(parsed.toUpdate));
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("[BulkAddUpdateLicenses][API_ERROR] - ", err);
      const apiMsg = err?.response?.data?.errors;
      setSubmitError(
        Array.isArray(apiMsg)
          ? apiMsg.join(", ")
          : apiMsg || err?.message || "Failed to apply changes"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        onClose={() => !submitting && onClose()}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className="inline-block align-bottom bg-white rounded-lg px-6 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:p-6"
              style={{ minWidth: "560px", maxWidth: "720px" }}
            >
              <Dialog.Title
                as="h3"
                className="text-lg leading-6 font-medium sapphire"
              >
                Bulk Add / Update State Medical Licenses
              </Dialog.Title>

              <p className="mt-2 text-sm text-gray-500">
                Paste tab-separated rows from a spreadsheet. Each row must be:
                <span className="font-mono">
                  {" "}
                  State&nbsp;Name&nbsp;⇥&nbsp;License&nbsp;#&nbsp;⇥&nbsp;Issue&nbsp;Date&nbsp;⇥&nbsp;Expiry&nbsp;Date
                </span>
                . Rows whose state already has a license are updated; new states
                are added.
              </p>

              <div className="mt-4">
                <label
                  htmlFor="bulk-paste"
                  className="block text-sm font-medium light_grayish_navy mb-1"
                >
                  Spreadsheet rows
                </label>
                <textarea
                  id="bulk-paste"
                  value={pastedText}
                  onChange={(e) => setPastedText(e.target.value)}
                  disabled={submitting || statesLoading}
                  rows={8}
                  placeholder={`Florida\tME151778\t07/12/2021\t01/31/2027\nGeorgia\t110131\t01/16/2026\t07/31/2028`}
                  className="w-full font-mono text-sm border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-blue-chill"
                />
              </div>

              {statesLoading && (
                <div className="mt-3 flex items-center gap-2 text-sm light_grayish_navy">
                  <Spinner /> <span>Loading states…</span>
                </div>
              )}

              <div className="mt-4 border border-gray-200 rounded-md p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm">
                    <div className="font-medium light_grayish_navy">
                      Default license document
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {defaultDocUrl
                        ? "Used as the document for every newly added license. Saved on this browser."
                        : "Required for new licenses. Upload once — we'll remember it on this browser."}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {uploadingDoc ? (
                      <div className="flex items-center gap-1 text-xs light_grayish_navy">
                        <Spinner width="4" height="4" />
                        <span>Uploading…</span>
                      </div>
                    ) : defaultDocUrl ? (
                      <>
                        <a
                          href={defaultDocUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-light underline"
                        >
                          View
                        </a>
                        <label
                          htmlFor="bulk-default-doc-input"
                          className="text-xs text-blue-light underline cursor-pointer"
                        >
                          Change
                        </label>
                        <button
                          type="button"
                          onClick={clearDefaultDoc}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Clear
                        </button>
                      </>
                    ) : (
                      <label
                        htmlFor="bulk-default-doc-input"
                        className="text-xs text-blue-light underline cursor-pointer"
                      >
                        Upload
                      </label>
                    )}
                    <input
                      id="bulk-default-doc-input"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleDefaultDocSelect}
                      disabled={uploadingDoc || submitting}
                      className="sr-only"
                    />
                  </div>
                </div>
                {defaultDocUrl && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-green-700">
                    <span>✓ Default document is set</span>
                  </div>
                )}
                {uploadError && (
                  <div className="mt-2 text-xs text-red-700">
                    {typeof uploadError === "string"
                      ? uploadError
                      : "Upload failed"}
                  </div>
                )}
              </div>

              {pastedText.trim() && !statesLoading && (
                <div className="mt-4 border border-gray-200 rounded-md p-3 bg-gray-50 max-h-60 overflow-y-auto">
                  <div className="text-sm font-medium sapphire mb-2">
                    Preview
                    <span className="ml-2 font-normal light_grayish_navy">
                      ({parsed.toAdd.length} to add, {parsed.toUpdate.length} to
                      update
                      {parsed.errors.length > 0 &&
                        `, ${parsed.errors.length} error${
                          parsed.errors.length === 1 ? "" : "s"
                        }`}
                      )
                    </span>
                  </div>

                  {parsed.toAdd.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">
                        Add
                      </div>
                      <ul className="text-xs text-gray-700 space-y-0.5">
                        {parsed.toAdd.map((row, i) => (
                          <li key={`add-${i}`} className="font-mono">
                            {row._stateName} · {row.licenseNumber} ·{" "}
                            {row.licensedDate} → {row.licenseExpiration}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {parsed.toUpdate.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">
                        Update
                      </div>
                      <ul className="text-xs text-gray-700 space-y-0.5">
                        {parsed.toUpdate.map((row, i) => (
                          <li key={`upd-${i}`} className="font-mono">
                            {row._stateName} · {row.licenseNumber} ·{" "}
                            {row.licensedDate} → {row.licenseExpiration}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {parsed.errors.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">
                        Errors
                      </div>
                      <ul className="text-xs text-red-700 space-y-0.5">
                        {parsed.errors.map((err, i) => (
                          <li key={`err-${i}`}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <p className="mt-3 text-xs italic light_grayish_navy">
                Note: existing documents are preserved on update. Newly added
                licenses use the default document above — replace per-state
                later via the Edit form if needed.
              </p>

              {needsDefaultDoc && (
                <div className="mt-2 text-xs text-amber-700">
                  Upload a default license document to enable adding new
                  licenses.
                </div>
              )}

              {submitError && (
                <div className="mt-3 rounded-md bg-red-100 border border-red-300 p-2 text-sm text-red-800">
                  {submitError}
                </div>
              )}

              <div className="mt-5 flex justify-end gap-2">
                <Button
                  className="h-9 px-4 bg-white border border-gray-300 saphire"
                  variant="Secondary"
                  btnText="Cancel"
                  isDisabled={submitting}
                  onClick={onClose}
                />
                <Button
                  className="h-9 px-4"
                  btnText={
                    parsed.toAdd.length > 0 && parsed.toUpdate.length > 0
                      ? `Add ${parsed.toAdd.length}, Update ${parsed.toUpdate.length}`
                      : parsed.toAdd.length > 0
                      ? `Add ${parsed.toAdd.length}`
                      : parsed.toUpdate.length > 0
                      ? `Update ${parsed.toUpdate.length}`
                      : "Apply"
                  }
                  isDisabled={!canApply}
                  isLoading={submitting}
                  onClick={handleApply}
                />
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
